import { useMutation } from '@tanstack/react-query';
import axios from '../../libraries/axios/axios';
import {
  getLocalStorageValue,
  LocalStorageKey,
} from '../../utils/localStorage/localStorage';
import { useSetRecoilState } from 'recoil';
import {
  currentVatRateState,
  DEFAULT_VAT_RATE,
} from '../../atoms/vat-rate/vat-rate.atom';
import { VatRate } from '../../domain/VatRate';
import { getVatRate } from '../../utils/vat/vat.util';

function getPosition(options?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, options),
  );
}

export const useInitCurrentVatRateMutation = () => {
  const setCurrentVatRate = useSetRecoilState(currentVatRateState);

  const fetchCurrentVatRate = async (vatRates: VatRate[]): Promise<VatRate> => {
    const storageVatRate = getLocalStorageValue<VatRate>(
      LocalStorageKey.vatRate,
    );

    if (storageVatRate) {
      return storageVatRate;
    }

    if (!navigator.geolocation) {
      return DEFAULT_VAT_RATE;
    }

    const { latitude, longitude } = (
      await getPosition({
        enableHighAccuracy: false,
        maximumAge: Infinity,
      })
    ).coords;

    const { data } = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
    );

    if (data.address && data.address.country_code) {
      const country = data.address.country_code.toUpperCase();

      return getVatRate(vatRates, country);
    }

    return DEFAULT_VAT_RATE;
  };

  return useMutation<VatRate, void, VatRate[]>({
    mutationFn: fetchCurrentVatRate,
    retry: false,
    onSuccess: (vatRate) => {
      setCurrentVatRate(vatRate);
    },
    onError: () => {
      setCurrentVatRate(DEFAULT_VAT_RATE);
    },
  });
};
